"use client";

import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";
import { CANDLES } from "./candles";

const PAD = 48;
const BLEED = 8;
const CANDLES_WIDTH = CANDLES.w + BLEED;
const CANDLES_HEIGHT = CANDLES.h + PAD;

const SPEED = 9;
const INTRO = 1.6;
const NEW_FADE = 1.2;
const GREEN = CANDLES.bars[0].col;
const RED = "#E5484D";
const LEVEL = "255,255,255";
const LEVEL_ALPHA = 0.16;

type LiveBar = {
  order: number;
  dev: number;
  h: number;
  up: number;
  dn: number;
  col: string;
  x0: number;
  cx: number;
  born?: number;
};

export default function HeroCandles({ className }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const R = CANDLES.bars;
    const cx = (i: number) => R[i].x + R[i].w / 2;
    const mid = (i: number) => R[i].y + R[i].h / 2;
    const first = cx(0);
    const step = cx(1) - cx(0);
    const bodyW = R[0].w;
    const last = R.length - 1;
    const rightEdge = cx(last);

    const smooth = R.map((_, i) => {
      let sum = 0;
      let count = 0;
      for (let j = Math.max(0, i - 3); j <= Math.min(last, i + 3); j++) {
        sum += mid(j);
        count++;
      }
      return sum / count;
    });
    const trend = (x: number) => {
      const f = (x - first) / step;
      if (f <= 0) return smooth[0] + f * (smooth[1] - smooth[0]);
      if (f >= last) return smooth[last] + (f - last) * (smooth[last] - smooth[last - 1]);
      const i = Math.floor(f);
      return smooth[i] + (f - i) * (smooth[i + 1] - smooth[i]);
    };
    const alphaAt = (x: number) => R[Math.max(0, Math.min(last, Math.round((x - first) / step)))].a;

    let seed = 7;
    const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;

    const bars: LiveBar[] = R.map((b, i) => ({
      order: i,
      dev: mid(i) - smooth[i],
      h: b.h,
      up: b.wt[1],
      dn: b.wb[1],
      col: b.col,
      x0: cx(i),
      cx: cx(i),
    }));

    const fadeRamp = (x0: number, span: number, rgb: string, alpha: number) => {
      const ramp = ctx.createLinearGradient(x0, 0, x0 + span * CANDLES.fade, 0);
      ramp.addColorStop(0, `rgba(${rgb},0)`);
      ramp.addColorStop(1, `rgba(${rgb},${alpha})`);
      return ramp;
    };
    const zones = CANDLES.zones.map((z) => ({
      ...z,
      w: z.x + z.w >= CANDLES.w ? z.w + BLEED : z.w,
      fill: fadeRamp(z.x, z.w, z.rgb, z.a),
    }));
    const { levels } = CANDLES;
    const levelStroke = fadeRamp(levels.x0, CANDLES.w - levels.x0, LEVEL, LEVEL_ALPHA);
    const body = (x: number, y: number, w: number, h: number) => {
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, 1);
        ctx.fill();
      } else {
        ctx.fillRect(x, y, w, h);
      }
    };

    let scale = 1;
    let dpr = 1;
    const resize = () => {
      const width = canvas.clientWidth;
      if (!width) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      scale = width / CANDLES_WIDTH;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(canvas.clientHeight * dpr);
    };

    const draw = (t: number) => {
      const intro = Math.min(1, t / INTRO);
      const shift = reduce ? 0 : Math.max(0, t - INTRO) * SPEED;
      bars.forEach((b) => {
        b.cx = b.x0 - shift;
      });
      while (bars[0].cx < first - step) {
        const tail = bars[bars.length - 1];
        const born: LiveBar = {
          order: -1,
          dev: (rnd() - 0.5) * 22,
          h: 6 + rnd() * 26,
          up: 4 + rnd() * 14,
          dn: 4 + rnd() * 12,
          col: rnd() > 0.32 ? GREEN : RED,
          x0: tail.x0 + step,
          cx: tail.x0 + step - shift,
          born: t,
        };
        bars.shift();
        bars.push(born);
      }

      const k = dpr * scale;
      ctx.setTransform(k, 0, 0, k, 0, PAD * k);
      ctx.clearRect(0, -PAD, CANDLES_WIDTH, CANDLES_HEIGHT);

      ctx.globalAlpha = Math.min(1, intro * 1.5);
      zones.forEach((z) => {
        ctx.fillStyle = z.fill;
        ctx.fillRect(z.x, z.y, z.w, z.h);
      });
      ctx.strokeStyle = levelStroke;
      ctx.lineWidth = 1;
      ctx.setLineDash([levels.dash, levels.dash]);
      ctx.lineDashOffset = levels.dash / 2;
      levels.y.forEach((y) => {
        ctx.beginPath();
        ctx.moveTo(levels.x0, y);
        ctx.lineTo(CANDLES_WIDTH, y);
        ctx.stroke();
      });
      ctx.setLineDash([]);

      bars.forEach((b, i) => {
        const live = b.cx > rightEdge - step * 0.5;
        const age = b.born != null ? Math.min(1, (t - b.born) / NEW_FADE) : 1;
        const reveal = b.order >= 0 ? Math.min(1, Math.max(0, (intro * 1.5 - b.order / R.length) * 3)) : 1;
        const a = alphaAt(b.cx) * age;
        if (a <= 0.002 || reveal <= 0 || b.cx > CANDLES_WIDTH + bodyW) return;
        const y = trend(b.cx) + b.dev;
        const pulse = reduce ? 1 : 1 + (live ? Math.sin(t * 2.4 + i) * 0.28 : Math.sin(t * 0.7 + i * 1.3) * 0.05);
        const h = b.h * pulse * reveal;
        const up = b.up * pulse * reveal;
        const dn = b.dn * reveal;
        ctx.globalAlpha = a;
        ctx.fillStyle = b.col;
        ctx.fillRect(b.cx - 1, y - h / 2 - up, 2, up);
        ctx.fillRect(b.cx - 1, y + h / 2, 2, dn);
        body(b.cx - bodyW / 2, y - h / 2, bodyW, h);
      });
      ctx.globalAlpha = 1;
    };

    resize();
    const observer = new ResizeObserver(() => {
      resize();
      if (reduce) draw(INTRO);
    });
    observer.observe(canvas);

    if (reduce) {
      draw(INTRO);
      return () => observer.disconnect();
    }

    let pointerX = 0;
    let pointerY = 0;
    let offsetX = 0;
    let offsetY = 0;
    const onPointer = (event: PointerEvent) => {
      pointerX = event.clientX / window.innerWidth - 0.5;
      pointerY = event.clientY / window.innerHeight - 0.5;
    };
    window.addEventListener("pointermove", onPointer, { passive: true });

    const start = performance.now();
    let frame = 0;
    let visible = true;
    const tick = (now: number) => {
      draw((now - start) / 1000);
      offsetX += (pointerX - offsetX) * 0.05;
      offsetY += (pointerY - offsetY) * 0.05;
      canvas.style.translate = `${(offsetX * -14 * scale).toFixed(2)}px ${(offsetY * -8 * scale).toFixed(2)}px`;
      frame = visible ? requestAnimationFrame(tick) : 0;
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !frame) frame = requestAnimationFrame(tick);
    });
    io.observe(canvas);
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      io.disconnect();
      observer.disconnect();
      window.removeEventListener("pointermove", onPointer);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden
      className={cn("pointer-events-none block", className)}
    />
  );
}
